import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { filter, finalize, Subscription } from 'rxjs';
import { QrCodeScannerComponent } from '../../shared/components/qr-code-scanner/qr-code-scanner.component';
import { getLandingRoute } from '../../shared/constants/role-access';
import { AuthService } from '../../shared/services/auth.service';
import { ToastService } from '../../shared/services/toast.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, QrCodeScannerComponent],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnInit, OnDestroy, AfterViewInit {
  public loginForm!: FormGroup;
  public isLoading = false;
  public currentYear = new Date().getFullYear();
  public showScanner = false;
  private subscriptions: Subscription = new Subscription();

  @ViewChild('qrTokenInput') qrTokenInput!: ElementRef<HTMLInputElement>;
  @ViewChild(QrCodeScannerComponent) private scanner?: QrCodeScannerComponent;

  constructor(
    private toastService: ToastService,
    private router: Router,
    private authService: AuthService
  ) { }

  ngAfterViewInit(): void {
    this.qrTokenInput.nativeElement.focus();
  }

  ngOnInit(): void {
    // Redirect to the role-appropriate landing page once the current user is known
    // (covers both an already-authenticated visit and a successful login).
    this.subscriptions.add(
      this.authService.currentUser$.pipe(filter(user => !!user)).subscribe(user => {
        this.router.navigate([getLandingRoute(user!.role)]);
      })
    );

    this.initForm();
  }

  private initForm(): void {
    this.loginForm = new FormGroup({
      qrToken: new FormControl('', {
        validators: [Validators.required],
        nonNullable: true,
      }),
    });
  }

  public getErrorMessage(controlName: string): string {
    const control = this.loginForm.get(controlName);
    if (!control || !control.errors || !control.touched) return '';
    if (control.errors['required']) return 'Dieses Feld ist erforderlich';
    return '';
  }

  public toggleScanner(): void {
    if (this.showScanner) {
      this.showScanner = false;
      this.scanner?.stopScanner();
      this.loginForm.get('qrToken')?.markAsUntouched();
      return;
    }

    this.loginForm.get('qrToken')?.markAsUntouched();
    this.showScanner = true;
  }

  public onScanSuccess(result: string): void {
    this.showScanner = false;
    this.loginForm.patchValue({ qrToken: result });
    this.onSubmit();
  }

  public onScannerClosed(): void {
    this.showScanner = false;
    this.loginForm.get('qrToken')?.markAsUntouched();
  }

  public onSubmit(): void {
    if (this.loginForm.valid) {
      this.isLoading = true;
      const { qrToken } = this.loginForm.value;

      this.subscriptions.add(
        this.authService
          .loginWithQrToken(qrToken)
          .pipe(finalize(() => (this.isLoading = false)))
          .subscribe({
            next: () => {
              // Navigation is handled by the currentUser$ subscription in ngOnInit,
              // which redirects to the correct landing page once the user's role is loaded.
            },
            error: (error: Error) => {
              let customErrorMessage = error.message;
              if (error.message === 'qrToken is incorrect!') {
                customErrorMessage = 'Ungültiger QR-Token';
              }
              this.toastService.showError('Login fehlgeschlagen: ' + customErrorMessage);
            },
          })
      );
    } else {
      this.toastService.showWarning('Bitte geben Sie einen QR-Token ein');
      Object.keys(this.loginForm.controls).forEach(key => {
        const control = this.loginForm.get(key);
        control?.markAsTouched();
      });
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
